import type {ReactNode} from 'react';

import {css} from '@emotion/react';
import React from 'react';
import {Outlet, ScrollRestoration} from 'react-router-dom';

import useViewer from '../hooks/useViewer';
import {useGlobalNavigationQuery} from '../lib/components/layout/__generated__/GlobalNavigation.graphql';
import {ViewerIDContext} from '../lib/components/layout/hooks/useViewerID';
import {DialogProvider} from '../store/DialogContext';
import {ModalProvider} from '../store/ModalContext';

type ProvidersProps = {
  children: ReactNode;
};

const Providers = (props: ProvidersProps) => {
  const {children} = props;
  const {viewer} = useViewer();

  // Some components use fetchPolicy: 'cache-only' for this query
  // Removing this will cause these components not to work as intended
  useGlobalNavigationQuery({
    fetchPolicy: 'cache-and-network',
  });

  return (
    <DialogProvider>
      <ModalProvider>
        <ViewerIDContext.Provider value={{id: viewer.id}}>
          {children}
        </ViewerIDContext.Provider>
      </ModalProvider>
    </DialogProvider>
  );
};

/**
 * BaseLayout is the root layout component providing global context and structure for the app.
 * Should be used in route configuration as the root layout for all pages.
 *
 * @component
 * @returns {JSX.Element} The rendered base layout.
 */
const BaseLayout = (): JSX.Element => {
  // Don't use `overflow: 'hidden'. Sticky elements will not work
  // Don't try to center the content.
  // It will override the default width child behavior, setting it to fit-content-like.
  const layoutCss = css({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100dvh',
  });

  return (
    <Providers>
      <ScrollRestoration />
      <div css={layoutCss}>
        <Outlet />
      </div>
    </Providers>
  );
};

export default BaseLayout;
